import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Eye, Edit, Trash2, Download } from 'lucide-react';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import JobForm from './JobForm';
import JobApplicationsView from './JobApplicationsView';
import { glassCard, glassCardStatic, glassTabsList } from '@/utils/admin-glass-styles';

interface JobOpening {
  id: string;
  title: string;
  location: string;
  department: string;
  employment_type: string;
  description: string;
  requirements: string;
  benefits: string;
  is_active: boolean;
  posted_date: string;
  application_deadline: string | null;
  created_at: string;
  updated_at: string;
}

const JobManagement: React.FC = () => {
  const { t } = useTranslation(['admin', 'careers']);
  const { toast } = useToast();
  const [jobs, setJobs] = useState<JobOpening[]>([]);
  const [loading, setLoading] = useState(true);
  const [showJobForm, setShowJobForm] = useState(false);
  const [editingJob, setEditingJob] = useState<JobOpening | null>(null);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const { data, error } = await supabase
        .from('job_openings')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setJobs(data || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast({
        title: t('admin:error'),
        description: t('admin:jobs.fetchError'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (job: JobOpening) => {
    try {
      const { error } = await supabase
        .from('job_openings')
        .update({ is_active: !job.is_active })
        .eq('id', job.id);

      if (error) throw error;

      setJobs(jobs.map(j => j.id === job.id ? { ...j, is_active: !j.is_active } : j));
      
      toast({
        title: t('admin:success'),
        description: t('admin:jobs.statusUpdated'),
      });
    } catch (error) {
      console.error('Error updating job status:', error);
      toast({
        title: t('admin:error'),
        description: t('admin:jobs.updateError'),
        variant: 'destructive',
      });
    }
  };

  const handleDeleteJob = async (job: JobOpening) => {
    if (!confirm(t('admin:jobs.confirmDelete', { title: job.title }))) {
      return;
    }

    try {
      const { error } = await supabase
        .from('job_openings')
        .delete()
        .eq('id', job.id);

      if (error) throw error;

      setJobs(jobs.filter(j => j.id !== job.id));
      
      toast({
        title: t('admin:success'),
        description: t('admin:jobs.deleted'),
      });
    } catch (error) {
      console.error('Error deleting job:', error);
      toast({
        title: t('admin:error'),
        description: t('admin:jobs.deleteError'),
        variant: 'destructive',
      });
    }
  };

  const handleEditJob = (job: JobOpening) => {
    setEditingJob(job);
    setShowJobForm(true);
  };

  const handleJobSaved = () => {
    setShowJobForm(false);
    setEditingJob(null);
    fetchJobs();
  };

  const getEmploymentTypeColor = (type: string) => {
    switch (type) {
      case 'full_time':
        return 'bg-green-100 text-green-800';
      case 'part_time':
        return 'bg-blue-100 text-blue-800';
      case 'contract':
        return 'bg-orange-100 text-orange-800';
      case 'internship':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-lg">{t('admin:loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">{t('admin:jobs.title')}</h2>
          <p className="text-muted-foreground">{t('admin:jobs.description')}</p>
        </div>
        <Button onClick={() => setShowJobForm(true)}>
          <Plus className="w-4 h-4 mr-2" />
          {t('admin:jobs.addNew')}
        </Button>
      </div>

      <Tabs defaultValue="jobs" className="space-y-6">
        <TabsList className={glassTabsList}>
          <TabsTrigger value="jobs">{t('admin:jobs.allJobs')}</TabsTrigger>
          <TabsTrigger value="applications">{t('admin:jobs.applications')}</TabsTrigger>
        </TabsList>

        <TabsContent value="jobs" className="space-y-4">
          {jobs.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <p className="text-muted-foreground">{t('admin:jobs.noJobs')}</p>
                <Button className="mt-4" onClick={() => setShowJobForm(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('admin:jobs.createFirst')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {jobs.map((job) => (
                <Card key={job.id} className={glassCardStatic}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-lg">{job.title}</CardTitle>
                          <Badge 
                            variant={job.is_active ? "default" : "secondary"}
                          >
                            {job.is_active ? t('admin:jobs.active') : t('admin:jobs.inactive')}
                          </Badge>
                          <Badge 
                            variant="outline"
                            className={getEmploymentTypeColor(job.employment_type)}
                          >
                            {t(`careers:employmentTypes.${job.employment_type}`)}
                          </Badge>
                        </div>
                        <CardDescription>
                          {job.location} • {job.department}
                        </CardDescription>
                        <p className="text-sm text-muted-foreground">
                          {t('admin:jobs.posted')}: {format(new Date(job.posted_date), 'MMM dd, yyyy')}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedJobId(job.id)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditJob(job)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleActive(job)}
                        >
                          {job.is_active ? t('admin:jobs.deactivate') : t('admin:jobs.activate')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteJob(job)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {job.description}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="applications">
          <JobApplicationsView />
        </TabsContent>
      </Tabs>

      {/* Job Form Dialog */}
      {showJobForm && (
        <JobForm
          job={editingJob}
          open={showJobForm}
          onOpenChange={setShowJobForm}
          onSave={handleJobSaved}
        />
      )}
    </div>
  );
};

export default JobManagement;